import styled from "@emotion/styled";
import { Fragment } from "react";

import { Droppable, FONT_WEIGHT_BOLD, showDndDebugInfo } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { DesignerEffect } from "../../types";
import { EFFECT_DROPPABLE_ZONE, EFFECT_SORTABLE_ZONE, EFFECT_TYPE, TYPE_ROOT_DROPPABLE } from "../constants";
import type { DroppableData } from "../dnd/dndSchemas";
import { EffectConnector } from "./EffectConnector";
import { EffectsListItem } from "./EffectsListItem";
import { EffectsListContainer, EffectsPlaceholderDropzone, ResponseZoneContainer, ResponseZoneHeader } from "./shared";

const HeaderPrefix = styled.span`
  font-weight: ${FONT_WEIGHT_BOLD};
  color: ${({ theme }) => theme.warning};
  margin-right: 4px;
`;

type Props = {
  effects: DesignerEffect[];
  onRemoveEffect: (effectId: number) => void;
};

export const SortableEffectsList = ({ effects, onRemoveEffect }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <ResponseZoneContainer>
      {showDndDebugInfo && `SortableZone: ${EFFECT_SORTABLE_ZONE}`}
      <ResponseZoneHeader>
        <HeaderPrefix>{formatMessage("headerPrefix")}</HeaderPrefix>
        {formatMessage("header")}
      </ResponseZoneHeader>
      <Droppable
        id={EFFECT_DROPPABLE_ZONE}
        data={{ rule: { ruleType: EFFECT_TYPE, type: TYPE_ROOT_DROPPABLE } } satisfies DroppableData}
        accepts={{ "rule.ruleType": [EFFECT_TYPE] }}
        color="warning"
      >
        {effects.length ? (
          <EffectsListContainer data-test="effects-list">
            {effects.map((effect, index) => (
              <Fragment key={effect.id}>
                <EffectConnector lineEnd="arrow" />
                <EffectsListItem effect={effect} index={index} onRemoveEffect={onRemoveEffect} />
              </Fragment>
            ))}
          </EffectsListContainer>
        ) : (
          <EffectsPlaceholderDropzone>{formatMessage("noEffectsMessage")}</EffectsPlaceholderDropzone>
        )}
      </Droppable>
    </ResponseZoneContainer>
  );
};

const formatStrings = {
  headerPrefix: {
    en: "Apply",
  },
  header: {
    en: "the following actions:",
  },
  noEffectsMessage: {
    en: "Drag actions here",
  },
} satisfies FormatMessages;
